<template>
  <div>
    <div class="flex flex-wrap items-center gap-2">
      <a v-if="anyPaymentRequired" :href="redirectToPaymentLink" target="_blank">
        <SecondaryButton class="bg-purple-100 hover:bg-purple-200 text-purple-700">
          <CreditCardIcon class="w-3 h-3 mr-1 shrink-0" />
          <span class="whitespace-nowrap text-base">{{ paymentText }}</span>
        </SecondaryButton>
      </a>
      <SecondaryButton
        v-if="declarationUpdateRequired"
        @click="$router.push(redirectToNewDeclarationVersionForm())"
        class="bg-purple-100 hover:bg-purple-200 text-purple-700"
      >
        <PencilIcon class="w-3 h-3 mr-1 shrink-0" />
        <span class="whitespace-nowrap text-base">{{ t("complete_declaration") }}</span>
      </SecondaryButton>
      <CustomerIbanAction v-if="customerIbanRequired" :claim="claim" />
      <SecondaryButton
        v-if="externalMerchantQuotationRequired"
        @click="openModal = true"
        class="bg-purple-100 hover:bg-purple-200 text-purple-700"
      >
        <DocumentTextIcon class="w-3 h-3 mr-1 shrink-0" />
        <span class="whitespace-nowrap text-base">{{ t("add_quotation") }}</span>
      </SecondaryButton>
      <SecondaryButton
        v-if="externalMerchantInvoiceRequired"
        @click="openModal = true"
        class="bg-purple-100 hover:bg-purple-200 text-purple-700"
      >
        <ReceiptTaxIcon class="w-3 h-3 mr-1 shrink-0" />
        <span class="whitespace-nowrap text-base">{{ t("add_invoice") }}</span>
      </SecondaryButton>
    </div>
    <QuotationUploadModal
      v-if="externalMerchantQuotationRequired"
      :open="openModal"
      :claim="claim"
      @close="onQuotationModalClose"
    />
    <InvoiceUploadModal
      v-if="externalMerchantInvoiceRequired"
      :open="openModal"
      :claim="claim"
      :cost-items="quotationCostItems"
      @close="onInvoiceModalClose"
    />
  </div>
</template>

<script>
import { CreditCardIcon, PencilIcon, DocumentTextIcon, ReceiptTaxIcon } from "@heroicons/vue/solid";
import { useI18n } from "vue-i18n";
import { mapActions, mapGetters } from "vuex";
import { SecondaryButton } from "@estaly/ui";
import QuotationUploadModal from "@/components/claim/details/information/action/quotations/QuotationUploadModal.vue";
import InvoiceUploadModal from "@/components/claim/details/information/action/invoices/InvoiceUploadModal.vue";
import CustomerIbanAction from "@/components/claim/details/action/CustomerIbanAction.vue";

export default {
  components: {
    SecondaryButton,
    CreditCardIcon,
    PencilIcon,
    DocumentTextIcon,
    ReceiptTaxIcon,
    QuotationUploadModal,
    InvoiceUploadModal,
    CustomerIbanAction,
  },

  props: ["claim"],

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      openModal: false,
    };
  },

  computed: {
    ...mapGetters("claims", [
      "finalSettlementPaymentRequired",
      "finalSettlementAndDeductiblePaymentRequired",
      "deductiblePaymentRequired",
      "anyPaymentRequired",
      "declarationUpdateRequired",
      "customerIbanRequired",
      "externalMerchantQuotationRequired",
      "externalMerchantInvoiceRequired",
    ]),
    paymentText() {
      if (this.finalSettlementAndDeductiblePaymentRequired) return this.t("payment_settlement_and_deductible");
      if (this.finalSettlementPaymentRequired) return this.t("payment_settlement");
      if (this.deductiblePaymentRequired) return this.t("payment_deductible");
      return "";
    },
    redirectToPaymentLink() {
      return "/claims/payment?claim_id=" + this.claim.id;
    },
    quotationCostItems() {
      return this.claim?.quotation?.costs;
    },
  },
  methods: {
    ...mapActions("claimQuotations", { fetchClaimQuotations: "fetchIndex" }),
    ...mapActions("claimInvoices", { fetchClaimInvoices: "fetchIndex" }),

    async onQuotationModalClose() {
      this.openModal = false;
      await this.fetchClaimQuotations({ claimId: this.claim.id });
    },

    async onInvoiceModalClose() {
      this.openModal = false;
      await this.fetchClaimInvoices({ claimId: this.claim.id });
    },

    redirectToNewDeclarationVersionForm() {
      return `/claims/${this.claim.friendlyId}/declarations/new-version`;
    },
  },
};
</script>

<i18n src="./RequiredAction.i18n.json"></i18n>
